import {
  Box, Card, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Button, TextField, InputAdornment, CircularProgress, Alert,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Tooltip,
} from '@mui/material';
import { Warning, CalendarMonth, Search, WhatsApp, Edit } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

const urgencyChip = (days) => {
  if (days < 0) return <Chip label="EXPIRED" color="error" size="small" icon={<Warning />} />;
  if (days <= 7) return <Chip label={`${days}d left`} color="error" size="small" />;
  if (days <= 30) return <Chip label={`${days}d left`} color="warning" size="small" />;
  return <Chip label={`${days}d left`} color="default" size="small" />;
};

function EditExpiryDialog({ item, open, onClose, onSaved }) {
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && item) setDate(item.expiry_date ? item.expiry_date.split('T')[0] : '');
  }, [open, item]);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/inventory/expiry', { product_id: item.id, branch_id: item.branch_id, expiry_date: date || null });
      onSaved();
      onClose();
    } catch (e) { alert(e.response?.data?.message || 'Failed'); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit Expiry Date — {item?.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Branch: {item?.branch_name} | Stock: {item?.stock}
        </Typography>
        <TextField fullWidth size="small" label="Expiry Date" type="date"
          value={date} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Update Expiry'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ExpiryPage() {
  const dispatch = useDispatch();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(90);
  const [search, setSearch] = useState('');
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/inventory/expiry', { params: { days } });
      setProducts(data.data || []);
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const filtered = products.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || (p.sku || '').toLowerCase().includes(search.toLowerCase())
  );

  const expired = filtered.filter(p => p.days_until_expiry < 0);
  const critical = filtered.filter(p => p.days_until_expiry >= 0 && p.days_until_expiry <= 7);
  const warning = filtered.filter(p => p.days_until_expiry > 7 && p.days_until_expiry <= 30);
  const upcoming = filtered.filter(p => p.days_until_expiry > 30);

  const sendWhatsAppAlert = async () => {
    const phone = prompt('Enter manager phone number (e.g. 923001234567):');
    if (!phone) return;
    try {
      const lines = expired.concat(critical).slice(0, 15).map(p =>
        `• ${p.name} — ${p.days_until_expiry < 0 ? 'EXPIRED' : `${p.days_until_expiry} days`} (Qty: ${p.stock})`
      ).join('\n');
      await api.post('/whatsapp/send', {
        phone,
        message: `⚠️ *Expiry Alert* — ${new Date().toLocaleDateString('en-PK')}\n\n${lines}\n\nPlease take action immediately.`,
      });
      dispatch(showSnackbar({ message: 'Alert sent via WhatsApp', severity: 'success' }));
    } catch {
      dispatch(showSnackbar({ message: 'WhatsApp send failed', severity: 'error' }));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CalendarMonth color="warning" />
          <Typography variant="h5" fontWeight={700}>Product Expiry / میعاد ختم</Typography>
        </Box>
        {(expired.length + critical.length) > 0 && (
          <Button variant="outlined" color="success" startIcon={<WhatsApp />} onClick={sendWhatsAppAlert}
            sx={{ borderColor: '#25D366', color: '#25D366' }}>
            Send WA Alert ({expired.length + critical.length} urgent)
          </Button>
        )}
      </Box>

      {/* Summary chips */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {expired.length > 0 && <Alert severity="error" sx={{ flex: 1, minWidth: 200 }}><strong>{expired.length}</strong> items EXPIRED — dispose immediately</Alert>}
        {critical.length > 0 && <Alert severity="warning" sx={{ flex: 1, minWidth: 200 }}><strong>{critical.length}</strong> items expiring within 7 days</Alert>}
        {warning.length > 0 && <Alert severity="info" sx={{ flex: 1, minWidth: 200 }}><strong>{warning.length}</strong> items expiring within 30 days</Alert>}
        {filtered.length === 0 && !loading && <Alert severity="success" sx={{ flex: 1 }}>No expiring products in the selected window 🎉</Alert>}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Show expiring within</InputLabel>
            <Select value={days} onChange={e => setDays(+e.target.value)} label="Show expiring within">
              <MenuItem value={7}>7 days</MenuItem>
              <MenuItem value={30}>30 days</MenuItem>
              <MenuItem value={60}>60 days</MenuItem>
              <MenuItem value={90}>90 days</MenuItem>
              <MenuItem value={180}>180 days</MenuItem>
              <MenuItem value={9999}>All with expiry</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" placeholder="Search product / SKU…" value={search}
            onChange={e => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />
        </Box>
      </Card>

      {/* Table */}
      <Card>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Branch</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Stock Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Expiry Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No items found</TableCell></TableRow>
              )}
              {filtered.map((p, i) => {
                const rowBg = p.days_until_expiry < 0 ? '#fef2f2' :
                              p.days_until_expiry <= 7 ? '#fff7ed' :
                              p.days_until_expiry <= 30 ? '#fefce8' : 'inherit';
                return (
                  <TableRow key={i} sx={{ bgcolor: rowBg }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace" color="text.secondary">{p.sku || '—'}</Typography>
                    </TableCell>
                    <TableCell>{p.category || '—'}</TableCell>
                    <TableCell>{p.branch_name || '—'}</TableCell>
                    <TableCell align="right"><Typography fontWeight={700}>{fmt(p.stock)}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {new Date(p.expiry_date).toLocaleDateString('en-PK')}
                      </Typography>
                    </TableCell>
                    <TableCell>{urgencyChip(p.days_until_expiry)}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit expiry date">
                        <IconButton size="small" onClick={() => setEditItem(p)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
      <EditExpiryDialog item={editItem} open={!!editItem} onClose={() => setEditItem(null)} onSaved={load} />
    </Box>
  );
}
